// ---------------------------------------------------------------------------
// Fetch-based API client with MSAL auth.
//
// Usage (inside a hook or provider):
//   const client = createApiClient(getToken);
//   const service = new JudgeService(client);
// ---------------------------------------------------------------------------

import { config } from '../config';

import { AppError } from './errors';

/** Per-request options for write methods. */
export interface RequestOptions {
  /** Extra headers merged on top of the client defaults (Authorization, Accept, Content-Type). */
  headers?: Record<string, string>;
  /** Abort signal forwarded to `fetch`, so callers can cancel in-flight requests. */
  signal?: AbortSignal;
}

export interface HttpResponse<T> {
  data: T;
  status: number;
  headers?: Headers;
}

/** Typed HTTP client interface consumed by services. */
export interface IHttpClient {
  get<T>(
    this: void,
    url: string,
    options?: RequestOptions,
  ): Promise<HttpResponse<T>>;
  /**
   * Fetch a non-JSON response body as plain text. Used for blobs whose
   * content-type is not application/json (e.g. .gml graph files served by
   * `GET /Jobs/{id}/blobs/{name}`).
   */
  getText(this: void, url: string): Promise<HttpResponse<string>>;
  post<T>(
    this: void,
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<HttpResponse<T>>;
  put<T>(
    this: void,
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<HttpResponse<T>>;
  patch<T>(
    this: void,
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<HttpResponse<T>>;
  // Body parameter supports APIs like DELETE /testsets/{id}/data that require
  // resource IDs in the request body (per OpenAPI spec).
  delete<T>(
    this: void,
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<HttpResponse<T>>;
}

/**
 * Create a fetch-based API client that injects a Bearer token via the
 * supplied `getMsalToken` callback before every request.
 *
 * Errors are normalised into {@link AppError} so consumers never need
 * to inspect raw Response objects.
 */
export function createApiClient(
  getMsalToken: () => Promise<string>,
  baseUrl?: string,
): IHttpClient {
  const baseURL = (baseUrl ?? config.apiBaseUrl).replace(/\/+$/, '');

  async function request<T>(
    url: string,
    init: RequestInit = {},
  ): Promise<HttpResponse<T>> {
    const token = await getMsalToken();

    const headers: HeadersInit = {
      Accept: 'application/json',
      ...init.headers,
    };
    if (token !== '') {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${baseURL}/${url}`, {
      ...init,
      headers,
    }).catch((err: unknown) => {
      throw new AppError(
        err instanceof Error ? err.message : 'Network error',
        'NETWORK_ERROR',
      );
    });

    if (!response.ok) {
      const data = (await response.json().catch(() => null)) as unknown;
      const message = getErrorMessage(data);
      throw new AppError(message, `HTTP_${response.status}`, response.status);
    }

    if (
      response.status === 204 ||
      response.headers.get('Content-Length') === '0'
    ) {
      return {
        data: undefined as T,
        status: response.status,
        headers: response.headers,
      };
    }

    const text = await response.text();
    const data = (
      text.trim().length > 0 ? (JSON.parse(text) as T) : undefined
    ) as T;
    return { data, status: response.status, headers: response.headers };
  }

  async function requestText(
    url: string,
    init: RequestInit = {},
  ): Promise<HttpResponse<string>> {
    const token = await getMsalToken();

    const headers: HeadersInit = {
      Accept: 'text/plain, application/octet-stream, */*',
      ...init.headers,
    };
    if (token !== '') {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${baseURL}/${url}`, {
      ...init,
      headers,
    }).catch((err: unknown) => {
      throw new AppError(
        err instanceof Error ? err.message : 'Network error',
        'NETWORK_ERROR',
      );
    });

    if (!response.ok) {
      // Best-effort error body parse — server may return JSON ProblemDetails
      // even for text endpoints.
      const errBody = (await response.json().catch(() => null)) as unknown;
      const message = getErrorMessage(errBody);
      throw new AppError(message, `HTTP_${response.status}`, response.status);
    }

    const data = await response.text();
    return { data, status: response.status, headers: response.headers };
  }

  return {
    get<T>(url: string, options?: RequestOptions) {
      return request<T>(url, {
        headers: options?.headers,
        signal: options?.signal,
      });
    },
    getText(url: string) {
      return requestText(url);
    },
    post<T>(url: string, body?: unknown, options?: RequestOptions) {
      return request<T>(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        body: body != null ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
    },
    put<T>(url: string, body?: unknown, options?: RequestOptions) {
      return request<T>(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        body: body != null ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
    },
    patch<T>(url: string, body?: unknown, options?: RequestOptions) {
      return request<T>(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        body: body != null ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
    },
    delete<T>(url: string, body?: unknown, options?: RequestOptions) {
      return request<T>(url, {
        method: 'DELETE',
        ...(body != null
          ? {
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            }
          : {}),
        signal: options?.signal,
      });
    },
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getErrorMessage(data: unknown): string {
  if (data != null && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (typeof record.message === 'string') return record.message;
    if (typeof record.ErrorMessage === 'string') return record.ErrorMessage;
    if (typeof record.title === 'string') return record.title;
    if (typeof record.error === 'string') return record.error;
    if (record.error != null && typeof record.error === 'object') {
      const nestedError = record.error as Record<string, unknown>;
      if (typeof nestedError.message === 'string') return nestedError.message;
    }
  }
  return 'An unexpected error occurred';
}
